import React, { useEffect, useState } from "react";
import { Field, Form, Formik } from "formik";
import { Input } from "components/Form/Input";
import IconLoader from "components/IconLoader/IconLoader";
import { AdvancedSelect } from "components/Form/AdvancedSelect";
import { StudentFieldArray, StudentFieldEnum } from "constants/enums/StudentFieldEnum";
import InputMaskField from "components/Form/InputMaskField";
import { Card, CardBody, CardHeader } from "components/Card/Card";
import * as Yup from "yup";
import { format } from "date-fns";
import { tr } from "date-fns/esm/locale";
import apiCall from "services/apiCall";
import {
  addSessionStudent,
  getOpenedSessionsByCityIdLevelIdSchoolIdAndExamId,
  getSessionApprovalFileURL,
  getSessionOpenedCities,
  getSessionOpenedExamsByCityIdLevelIdAndSchoolId,
  getSessionOpenedLevelsByCityId,
  getSessionOpenedSchoolsByCityIdAndLevelId,
  isExamAllowed,
  isSessionAllowed,
  saveSessionApprovalLog
} from "services/exam/session-service";
import showToast from "helpers/showToast";
import Dialog from "components/Dialogs/Dialog";
import { sendEnteranceInfoSms } from "services/announcement-service";
import { sort } from "fast-sort";
import SessionExamApprovalViewModal from "./SessionExamApprovalViewModal";

const formSchema = Yup.object().shape({
  city: Yup.object().required("FORM.REQUIRED"),
  level: Yup.object().required("FORM.REQUIRED"),
  field: Yup.object().when("level", {
    is: (level) => ["11", "12", "60"].includes(level.code),
    then: (schema) => schema.required("FORM.REQUIRED"),
    otherwise: (schema) => schema
  }),
  examSchool: Yup.object().required("FORM.REQUIRED"),
  examId: Yup.object().required("FORM.REQUIRED"),
  sessionId: Yup.object().required("FORM.REQUIRED"),
  studentNameAndSurname: Yup.string().required("FORM.REQUIRED"),
  studentPhone: Yup.string().matches(
    /^5\d{2} \d{3} \d{2} \d{2}$/,
    "Telefon numarası eksik veya hatalı girildi."
  ),
  studentCurrentSchoolType: Yup.object().required("FORM.REQUIRED"),
  studentCurrentSchool: Yup.string().required("FORM.REQUIRED"),
  parentNameAndSurname: Yup.string().required("FORM.REQUIRED"),
  parentPhone: Yup.string()
    .matches(/^5\d{2} \d{3} \d{2} \d{2}$/, "Telefon numarası eksik veya hatalı girildi.")
    .required("FORM.REQUIRED")
});

const defaultValues = {
  id: "",
  sessionId: "",
  examId: "",
  roomId: "",
  studentNameAndSurname: "",
  identityNumber: "",
  studentPhone: "",
  studentEmail: "",
  studentGender: "",
  studentCurrentSchool: "",
  studentCurrentSchoolType: "",
  field: "",
  level: "",
  city: "",
  examSchool: "",
  parentNameAndSurname: "",
  parentPhone: "",
  parentEmail: "",
  studentRoleId: "",
  deskNo: ""
};

const SchoolType = {
  KURS: "Kurs",
  KOLEJ_OKUL: "Kolej/Okul"
};

const SchoolTypes = Object.keys(SchoolType).map((item, index) => ({
  id: item,
  name: SchoolType[item]
}));

export default function RegisterFormCU({
  identityNumber,
  setSelectedApplication,
  changeMultiplePageMode,
  applicationList,
  setApplicationList
}) {
  const [formValues, setFormValues] = useState(defaultValues);
  const [showSessionDialog, setShowSessionDialog] = useState(false);
  const [showExamDialog, setShowExamDialog] = useState(false);
  //loadings
  const [formSubmitLoading, setFormSubmitLoading] = useState(false);
  const [cityLoading, setCityLoading] = useState(false);
  const [levelLoading, setLevelLoading] = useState(false);
  const [schoolLoading, setSchoolLoading] = useState(false);
  const [examLoading, setExamLoading] = useState(false);
  const [sessionLoading, setSessionLoading] = useState(false);
  //lists
  const [cityList, setCityList] = useState([]);
  const [levelList, setLevelList] = useState([]);
  const [schoolList, setSchoolList] = useState([]);
  const [examList, setExamList] = useState([]);
  const [sessionList, setSessionList] = useState([]);
  //approval modal
  const [approvalModalData, setApprovalModalData] = useState(null);

  useEffect(() => {
    getCitiesFromApi();
  }, []);

  const getCitiesFromApi = () => {
    setCityLoading(true);
    apiCall({
      service: getSessionOpenedCities,
      onSuccess: (res) => {
        setCityLoading(false);
        setCityList(sort(res?.data || [])?.asc((city) => city.plateNo) || []);
      },
      onError: () => {
        setCityLoading(false);
        showToast("Şehir arama işlemi sırasında hata oluştu", { type: "error" });
      }
    });
  };

  const handleCityChange = (city, setFieldValue) => {
    setFieldValue("city", city);
    setFieldValue("level", "");
    setFieldValue("field", "");
    setLevelList([]);
    setFieldValue("examSchool", "");
    setSchoolList([]);
    setFieldValue("examId", "");
    setExamList([]);
    setFieldValue("sessionId", "");
    setSessionList([]);
    setLevelLoading(true);
    apiCall({
      service: getSessionOpenedLevelsByCityId,
      serviceParams: { cityId: city?.id },
      onSuccess: (res) => {
        setLevelLoading(false);
        setLevelList(sort(res?.data || [])?.asc((level) => level?.order));
      },
      onError: () => {
        showToast("Kademe arama işlemi sırasında hata oluştu.", { type: "error" });
        setLevelLoading(false);
      }
    });
  };

  const handleLevelChange = (level, setFieldValue, values) => {
    setFieldValue("level", level);
    setFieldValue("field", "");
    setFieldValue("examSchoolType", "");
    setFieldValue("examSchool", "");
    setSchoolList([]);
    setFieldValue("examId", "");
    setExamList([]);
    setFieldValue("sessionId", "");
    setSessionList([]);
  };

  const handleSchoolTypeChange = (schoolType, setFieldValue, values) => {
    setFieldValue("examSchoolType", schoolType);
    setFieldValue("examSchool", "");
    setFieldValue("examId", "");
    setExamList([]);
    setFieldValue("sessionId", "");
    setSessionList([]);

    setSchoolLoading(true);
    apiCall({
      service: getSessionOpenedSchoolsByCityIdAndLevelId,
      serviceParams: {
        cityId: values?.city?.id,
        levelId: values?.level?.id,
        schoolType: schoolType?.id
      },
      onSuccess: (res) => {
        setSchoolList(sort(res?.data || [])?.asc((school) => school.campusName) || []);
        setSchoolLoading(false);
      },
      onError: () => {
        setSchoolLoading(false);
        showToast("Kurum arama işlemi sırasında hata oluştu.", { type: "error" });
      }
    });
  };

  const handleSchoolChange = (school, setFieldValue, values) => {
    setFieldValue("examSchool", school);
    setFieldValue("examId", "");
    setExamList([]);
    setFieldValue("sessionId", "");
    setSessionList([]);

    setExamLoading(true);
    apiCall({
      service: getSessionOpenedExamsByCityIdLevelIdAndSchoolId,
      serviceParams: {
        cityId: values?.city?.id,
        levelId: values?.level?.id,
        schoolId: school?.id
      },
      onSuccess: (res) => {
        setExamLoading(false);
        setExamList(sort(res?.data || []).asc((exam) => exam.name) || []);
      },
      onError: () => {
        setExamLoading(false);
        showToast("Sınav arama işlemi sırasında hata oluştu.", { type: "error" });
      }
    });
  };

  const handleExamChange = async (exam, setFieldValue, values) => {
    setSessionLoading(true);
    setFieldValue("sessionId", "");
    setSessionList([]);
    const isAllowedRes = await apiCall({
      service: isExamAllowed,
      serviceParams: {
        identityNumber: identityNumber,
        examId: exam?.id
      }
    });
    if (!isAllowedRes?.data) {
      setShowExamDialog(true);
      setSessionLoading(false);
      setFieldValue("examId", "");
    } else {
      setFieldValue("examId", exam);
      apiCall({
        service: getOpenedSessionsByCityIdLevelIdSchoolIdAndExamId,
        serviceParams: {
          cityId: values?.city?.id,
          levelId: values?.level?.id,
          schoolId: values?.examSchool?.id,
          examId: exam?.id
        },
        onSuccess: (res) => {
          setSessionLoading(false);
          setSessionList(
            sort(res?.data).asc((session) =>
              session?.sessionDate ? new Date(session?.sessionDate).getTime() : new Date().getTime()
            ) || []
          );
        },
        onError: () => {
          setSessionLoading(false);
          showToast("Seans arama işlemi sırasında hata oluştu.", { type: "error" });
        }
      });
    }
  };

  const handleSessionChange = (session, setFieldValue) => {
    apiCall({
      service: isSessionAllowed,
      serviceParams: {
        identityNumber: identityNumber,
        sessionId: session?.id
      },
      onSuccess: (res) => {
        if (!res?.data) {
          setShowSessionDialog(true);
          setFieldValue("sessionId", "");
        }
      }
    });
  };

  const getCityPlaceholder = () => {
    if (cityLoading) {
      return "Şehirler Yükleniyor...";
    } else if (cityList?.length > 0) {
      return "Şehir Seçiniz";
    } else {
      return "Sınav olan şehir bulunamadı.";
    }
  };

  const getLevelPlaceholder = (values) => {
    if (!values?.city?.id) {
      return "Şehir Seçimi Bekleniyor";
    } else if (levelLoading) {
      return "Kademeler Yükleniyor...";
    } else if (levelList?.length > 0) {
      return "Kademe Seçiniz";
    } else {
      return "Sınav olan kademe bulunamadı.";
    }
  };

  const getLevelDisabled = (values) => {
    return !values?.city?.id;
  };

  const getSchoolTypePlaceholder = (values) => {
    if (!values?.level?.id) {
      return "Kademe Seçimi Bekleniyor";
    } else {
      return "Kurum Tipi Seçiniz";
    }
  };

  const getSchoolTypeDisabled = (values) => {
    return !values?.level?.id;
  };

  const getSchoolPlaceholder = (values) => {
    if (!values?.examSchoolType?.id) {
      return "Kurum Tipi Seçimi Bekleniyor";
    } else if (schoolLoading) {
      return "Kurumlar Yükleniyor...";
    } else if (schoolList?.length > 0) {
      return "Kurum Seçiniz";
    } else {
      return "Sınav olan kurum bulunamadı.";
    }
  };

  const getSchoolDisabled = (values) => {
    return !values?.examSchoolType?.id;
  };

  const getExamPlaceholder = (values) => {
    if (!values?.examSchool?.id) {
      return "Kurum Seçimi Bekleniyor";
    } else if (examLoading) {
      return "Sınavlar Yükleniyor...";
    } else if (examList?.length > 0) {
      return "Sınav Seçiniz";
    } else {
      return "Sınav bulunamadı.";
    }
  };

  const getExamDisabled = (values) => {
    return !values?.examSchool?.id;
  };

  const getSessionPlaceholder = (values) => {
    if (!values?.examId?.id) {
      return "Sınav Seçimi Bekleniyor";
    } else if (sessionLoading) {
      return "Seanslar Yükleniyor...";
    } else if (sessionList?.length > 0) {
      return "Seans Seçiniz";
    } else {
      return "Seans bulunamadı.";
    }
  };

  const getSessionDisabled = (values) => {
    return !values?.examId?.id;
  };

  const sendInformationMessage = (data) => {
    apiCall({
      service: sendEnteranceInfoSms,
      serviceParams: data?.id
    });
  };

  const onSubmit = async (values, approvalState) => {
    setFormSubmitLoading(true);
    if (approvalState === "SHOW") {
      const approvalRes = await apiCall({
        service: getSessionApprovalFileURL,
        serviceParams: {
          schoolId: values.examSchool?.id,
          sessionId: values.sessionId?.id
        },
        onError: () => {
          showToast("Sınav yönergesi bulunamadı. Kurum ile iletişime geçiniz...", {
            type: "error"
          });
          setFormSubmitLoading(false);
        }
      });
      if (approvalRes?.data?.length) {
        setApprovalModalData({
          values: values,
          fileUrl: approvalRes?.data
        });
        setFormSubmitLoading(false);
        return;
      }
    } else {
      setApprovalModalData(null);
    }
    const params = {
      sessionId: values?.sessionId?.id,
      examId: values?.examId?.id,
      roomId: "",
      studentNameAndSurname: values?.studentNameAndSurname,
      identityNumber: identityNumber,
      studentPhone: values?.studentPhone,
      studentEmail: values?.studentEmail,
      studentCurrentSchool: values?.studentCurrentSchool,
      studentCurrentSchoolType: values?.studentCurrentSchoolType?.id,
      field: values?.field?.id,
      level: values?.level?.id,
      levelName: values?.level?.name,
      city: values?.city?.id,
      examSchool: values?.examSchool?.id,
      parentNameAndSurname: values?.parentNameAndSurname,
      parentPhone: values?.parentPhone,
      parentEmail: values?.parentEmail,
      studentRoleId: "",
      deskNo: "",
      applyDate: new Date()
    };
    apiCall({
      service: addSessionStudent,
      serviceParams: params,
      onSuccess: (res) => {
        setFormSubmitLoading(false);
        setFormValues(defaultValues);
        const modeList = [
          { name: "registerForm", open: false },
          { name: "identityCheck", open: !res?.data },
          { name: "validEntry", open: res?.data },
          { name: "applicationList", open: false }
        ];
        setSelectedApplication(res?.data);
        setApplicationList((prevState) => [...(prevState || []), res?.data]);
        changeMultiplePageMode(modeList)();
        showToast("Başvurunuz başarıyla oluşturuldu.", { type: "success" });
        sendInformationMessage(res.data);
        if (approvalState === "CONFIRM") {
          apiCall({
            service: saveSessionApprovalLog,
            serviceParams: res?.data?.id
          });
        }
      },
      onError: (res) => {
        setFormSubmitLoading(false);
        showToast("Başvuru ekleme işlemi sırasında hata oluştu.", { type: "error" });
      }
    });
  };

  const goBack = () => {
    const modeList = [
      { name: "registerForm", open: false },
      { name: "identityCheck", open: false },
      { name: "validEntry", open: false },
      { name: "applicationList", open: false },
      { name: "registerMenu", open: true }
    ];
    changeMultiplePageMode(modeList)();
  };

  return (
    <>
      <div className="container pt-6" style={{ position: "relative" }}>
        <button
          onClick={goBack}
          className="btn btn-primary d-none d-lg-block"
          style={{ position: "absolute", left: -85 }}
        >
          <IconLoader icon="mdiChevronLeft" size={1} />
        </button>
        <div className="w-100 d-flex justify-content-center">
          <img src="/media/register/registerTitleImage.png" style={{ maxWidth: "100%" }} />
        </div>
        <div className="d-flex flex-column align-items-center justify-content-center px-10 px-xl-40">
          <Card className="mt-10 w-100">
            <CardHeader title="Sınav Başvuru Formu" />
            <CardBody>
              <div className="container pt-4" style={{ position: "relative" }}>
                <Formik
                  initialValues={formValues}
                  onSubmit={(values) => {
                    onSubmit(values, "SHOW");
                  }}
                  validationSchema={formSchema}
                >
                  {({ values, setFieldValue }) => (
                    <Form>
                      <span className="lms-font-bold h5 mb-2">Öğrenci Eğitim Bilgileri</span>
                      <div className="row mt-2">
                        <div className="col-lg-3">
                          <Field
                            name="city"
                            label="Şehir*"
                            placeholder={getCityPlaceholder()}
                            isDisabled={cityLoading}
                            component={AdvancedSelect}
                            onChange={(city) => handleCityChange(city, setFieldValue, values)}
                            optionLabelProp="name"
                            optionValueProp="id"
                            optionsList={cityList}
                            customizedOptionFields={["id", "name"]}
                            withFeedbackLabel={true}
                          />
                        </div>
                        <div className="col-lg-3">
                          <Field
                            name="level"
                            label="Şu An Okuduğunuz Sınıf Seviyesi*"
                            placeholder={getLevelPlaceholder(values)}
                            isDisabled={getLevelDisabled(values)}
                            component={AdvancedSelect}
                            withFeedbackLabel={true}
                            onChange={(level) => handleLevelChange(level, setFieldValue, values)}
                            optionLabelProp="name"
                            optionValueProp="id"
                            optionsList={levelList}
                            customizedOptionFields={["id", "name", "code"]}
                          />
                        </div>
                        {["11", "12", "60"].includes(values?.level?.code) && (
                          <div className="col-lg-3">
                            <Field
                              name="field"
                              label="Alan*"
                              placeholder="Alan Seçiniz"
                              component={AdvancedSelect}
                              withFeedbackLabel={true}
                              optionLabelProp="name"
                              optionValueProp="id"
                              optionsList={StudentFieldArray?.filter(
                                (field) => ![StudentFieldEnum.TYT].includes(field?.id)
                              )}
                              customizedOptionFields={["id", "name"]}
                            />
                          </div>
                        )}
                        <div className="row px-4">
                          <div className="col-lg-4">
                            <Field
                              name="examSchoolType"
                              label="Sınava Gireceğiniz Kurum Tipi"
                              placeholder={getSchoolTypePlaceholder(values)}
                              isDisabled={getSchoolTypeDisabled(values)}
                              component={AdvancedSelect}
                              onChange={(schoolType) =>
                                handleSchoolTypeChange(schoolType, setFieldValue, values)
                              }
                              optionLabelProp={"name"}
                              optionValueProp="id"
                              optionsList={SchoolTypes}
                              customizedOptionFields={["id", "name"]}
                              withFeedbackLabel={true}
                            />
                          </div>
                          <div className="col-lg-12">
                            <Field
                              name="examSchool"
                              label="Sınava Girmek İstediğiniz Binayı Seçiniz* (Sınavı Yapan Kurum / Sınavın Yapılacağı Bina)"
                              placeholder={getSchoolPlaceholder(values)}
                              isDisabled={getSchoolDisabled(values)}
                              component={AdvancedSelect}
                              onChange={(school) =>
                                handleSchoolChange(school, setFieldValue, values)
                              }
                              optionLabelProp={["campusName", "name"]}
                              prefix=" / "
                              optionValueProp="id"
                              optionsList={schoolList}
                              customizedOptionFields={["id", "name", "campusName"]}
                              withFeedbackLabel={true}
                            />
                          </div>
                        </div>
                      </div>
                      <span className="lms-font-bold h5 mb-2 mt-6">Sınav ve Seans Seçimi</span>
                      <div className="row mt-2">
                        <div className="col-lg-3">
                          <Field
                            name="examId"
                            label="Sınav*"
                            isDisabled={getExamDisabled(values)}
                            placeholder={getExamPlaceholder(values)}
                            component={AdvancedSelect}
                            onChange={(exam) => handleExamChange(exam, setFieldValue, values)}
                            withFeedbackLabel={true}
                            optionLabelProp="name"
                            optionValueProp="id"
                            optionsList={examList}
                            customizedOptionFields={["id", "name"]}
                          />
                        </div>
                        <div className="col-lg-3">
                          <Field
                            name="sessionId"
                            label="Seans*"
                            isDisabled={getSessionDisabled(values)}
                            placeholder={getSessionPlaceholder(values)}
                            component={AdvancedSelect}
                            onChange={(session) =>
                              handleSessionChange(session, setFieldValue, values)
                            }
                            withFeedbackLabel={true}
                            optionLabelProp={["sessionDate", "hasEmptyRoom"]}
                            optionValueProp="id"
                            optionsList={
                              sessionList?.map((session) => ({
                                id: session?.id,
                                name: session?.name,
                                sessionDate: format(
                                  new Date(session?.sessionDate),
                                  "cccc dd/MM/yyyy HH:mm",
                                  {
                                    locale: tr
                                  }
                                ),
                                hasEmptyRoom:
                                  session?.applyCount >= session?.sessionCapacity ? " (Dolu)" : null
                              })) || []
                            }
                            optionDisableKey="hasEmptyRoom"
                            customizedOptionFields={["id", "name", "sessionDate"]}
                          />
                        </div>
                      </div>

                      <span className="lms-font-bold h5 mb-2">Öğrenci Bilgileri</span>
                      <div className="row mt-2">
                        <div className="col-lg-3">
                          <Field
                            name="studentNameAndSurname"
                            label="Öğrenci Adı Soyadı*"
                            placeholder="Öğrenci Adı Soyadı"
                            component={Input}
                            withFeedbackLabel={true}
                            isUpperCase
                          />
                        </div>
                        <div className="col-lg-3">
                          <InputMaskField
                            name="studentPhone"
                            label="Cep Telefonu"
                            placeholder="(___) ___ __ __"
                            format="### ### ## ##"
                            mask="_"
                          />
                        </div>
                        <div className="col-lg-3">
                          <Field
                            name="studentEmail"
                            label="E-posta Adresi"
                            placeholder="E-posta Adresi"
                            component={Input}
                            withFeedbackLabel={true}
                          />
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-lg-3">
                          <Field
                            name="studentCurrentSchoolType"
                            label="Şuan Devam Ettiğiniz Okulun Türü*"
                            placeholder="Okul Türü Seçiniz"
                            component={AdvancedSelect}
                            withFeedbackLabel={true}
                            optionLabelProp="name"
                            optionValueProp="id"
                            optionsList={[
                              {
                                id: "PRIVATE",
                                name: "Özel Okul"
                              },
                              {
                                id: "OFFICIAL",
                                name: "Devlet Okulu"
                              }
                            ]}
                            customizedOptionFields={["id", "name"]}
                          />
                        </div>
                        <div className="col-lg-3">
                          <Field
                            name="studentCurrentSchool"
                            label="Şuan Devam Ettiğiniz Okulun Adı*"
                            placeholder="Okul İsmi Giriniz"
                            component={Input}
                            withFeedbackLabel={true}
                            isUpperCase
                          />
                        </div>
                      </div>

                      <span className="lms-font-bold h5 mb-2 mt-6">Veli Bilgileri</span>
                      <div className="row mb-6">
                        <div className="col-lg-3">
                          <Field
                            name="parentNameAndSurname"
                            label="Veli Adı Soyadı*"
                            placeholder="Veli Adı Soyadı*"
                            component={Input}
                            withFeedbackLabel={true}
                            isUpperCase
                          />
                        </div>
                        <div className="col-lg-3">
                          <InputMaskField
                            name="parentPhone"
                            label="Cep Telefonu*"
                            placeholder="(___) ___ __ __"
                            format="### ### ## ##"
                            mask="_"
                          />
                        </div>
                        <div className="col-lg-3">
                          <Field
                            name="parentEmail"
                            label="Veli E-Posta Adresi"
                            placeholder="Veli E-Posta Adresi"
                            component={Input}
                            withFeedbackLabel={true}
                          />
                        </div>
                      </div>
                      <div className="d-flex justify-content-center my-4">
                        <div style={{ width: "35%" }}>
                          <button
                            className={`btn btn-primary btn-block ${
                              formSubmitLoading ? "spinner spinner-white spinner-left" : ""
                            }`}
                            disabled={formSubmitLoading}
                          >
                            Kaydet
                          </button>
                        </div>
                      </div>
                    </Form>
                  )}
                </Formik>
              </div>
            </CardBody>
          </Card>
        </div>
      </div>
      {showSessionDialog && (
        <Dialog
          show={showSessionDialog}
          onHide={() => {
            setShowSessionDialog(false);
          }}
          modalTitle="Bilgilendirme"
          modalMessage="Bu kurumda sınav kaydınız yapılamamaktadır. Lütfen kurum idarenize başvurunuz."
          okButtonTitle="Tamam"
          action={() => setShowSessionDialog(false)}
          showCancelButton={false}
        />
      )}
      {showExamDialog && (
        <Dialog
          show={showExamDialog}
          onHide={() => {
            setShowExamDialog(false);
          }}
          modalTitle="Bilgilendirme"
          modalMessage="Bu sınavda kaydınız bulunmaktadır."
          okButtonTitle="Tamam"
          action={() => setShowExamDialog(false)}
          showCancelButton={false}
        />
      )}

      {approvalModalData && (
        <SessionExamApprovalViewModal
          data={approvalModalData}
          onHide={() => setApprovalModalData(null)}
          show={true}
          onSuccess={(values) => {
            onSubmit(values, "CONFIRM");
          }}
        />
      )}
    </>
  );
}
